import { useState } from 'react';

import { OnboardingState } from '../../../types/OnboardingState';
import { Button } from '../../cdl/Button/Button';
import { Callout } from '../../cdl/Callout/Callout';
import { DropdownMenu } from '../../cdl/DropdownMenu/DropdownMenu';
import { IconButton } from '../../cdl/IconButton/IconButton';
import { useToasts } from '../../cdl/Toast/useToasts';
import { translate } from '../../common/helpers/translate.helper';
import { IconDots } from '../../common/icons/cdl/Dots';
import { IconDownload } from '../../common/icons/cdl/Download';
import { IconUpload } from '../../common/icons/cdl/Upload';
import { Box } from '../../common/ui/Box';
import { OfferModel } from '../../offer/model/OfferModel';
import { useDownloadCustomerOnboardingDocuments } from '../hooks/useDownloadCustomerOnboardingDocuments';
import { useRfqSupplierOnboarding } from '../hooks/useRfqSupplierOnboarding';

import { SupplierOnboardingUploadModal } from './SupplierOnboardingUploadModal';

interface SupplierOnboardingCalloutProps {
    supplierId: string;
    requestForQuoteId: string;
    canManage: boolean;
    offers: OfferModel[];
}

export const SupplierOnboardingCallout = ({
    supplierId,
    requestForQuoteId,
    canManage,
    offers,
}: SupplierOnboardingCalloutProps) => {
    const { data: onboarding } = useRfqSupplierOnboarding(requestForQuoteId);
    const downloadDocuments = useDownloadCustomerOnboardingDocuments();
    const { addErrorToast } = useToasts();
    const [showUploadModal, setShowUploadModal] = useState(false);

    if (!onboarding) {
        return null;
    }

    const downloadCustomerDocuments = () => {
        downloadDocuments.mutate(
            { offerId: onboarding.offerId },
            { onError: () => addErrorToast(translate('supplierOnboarding.downloadError')) }
        );
    };

    switch (onboarding.state) {
        case OnboardingState.REQUESTED:
            return (
                <Callout
                    heading={translate('supplierOnboarding.callout.requested.title')}
                    description={translate('supplierOnboarding.callout.requested.description')}
                >
                    {canManage ? (
                        <Button
                            emphasis="high"
                            LeadingIcon={IconDownload}
                            isLoading={downloadDocuments.isPending}
                            onClick={downloadCustomerDocuments}
                        >
                            {translate('supplierOnboarding.callout.downloadAction')}
                        </Button>
                    ) : null}
                </Callout>
            );

        case OnboardingState.IN_PROGRESS:
            return (
                <>
                    <Callout
                        heading={translate('supplierOnboarding.callout.inProgress.title')}
                        description={translate('supplierOnboarding.callout.inProgress.description')}
                    >
                        {canManage ? (
                            <Box display="flex" alignItems="center" gap={8}>
                                <Button
                                    emphasis="high"
                                    LeadingIcon={IconUpload}
                                    onClick={() => setShowUploadModal(true)}
                                >
                                    {translate('supplierOnboarding.callout.uploadAction')}
                                </Button>
                                <DropdownMenu
                                    align="end"
                                    trigger={<IconButton Icon={IconDots} emphasis="medium" />}
                                    options={[
                                        {
                                            label: translate('supplierOnboarding.callout.downloadAction'),
                                            onSelect: downloadCustomerDocuments,
                                        },
                                    ]}
                                />
                            </Box>
                        ) : null}
                    </Callout>
                    {showUploadModal ? (
                        <SupplierOnboardingUploadModal
                            offerId={onboarding.offerId}
                            supplierId={supplierId}
                            onboarding={onboarding}
                            isOpen={showUploadModal}
                            onDismiss={() => setShowUploadModal(false)}
                        />
                    ) : null}
                </>
            );

        case OnboardingState.SUBMITTED: {
            const onboardingOffer = offers.find((offer) => offer.id === onboarding.offerId);
            const isOfferClosed = !onboardingOffer || onboardingOffer.isCanceled();
            return (
                <>
                    <Callout
                        heading={translate('supplierOnboarding.callout.submitted.title')}
                        description={translate('supplierOnboarding.callout.submitted.description')}
                    >
                        {canManage && !isOfferClosed ? (
                            <Button LeadingIcon={IconUpload} onClick={() => setShowUploadModal(true)}>
                                {translate('supplierOnboarding.callout.addDocumentsAction')}
                            </Button>
                        ) : null}
                    </Callout>
                    {showUploadModal ? (
                        <SupplierOnboardingUploadModal
                            offerId={onboarding.offerId}
                            supplierId={supplierId}
                            onboarding={onboarding}
                            isOpen={showUploadModal}
                            onDismiss={() => setShowUploadModal(false)}
                        />
                    ) : null}
                </>
            );
        }

        case OnboardingState.CANCELED:
            return (
                <Callout
                    heading={translate('supplierOnboarding.callout.cancelled.title')}
                    description={translate('supplierOnboarding.callout.cancelled.description')}
                />
            );

        default:
            return null;
    }
};
